import React from 'react';
import FlexStack from "../components/Common/flex-stack";
import {withErrorBoundary} from 'react-error-boundary';
import {ErrorBox, InfoBox} from '../components/Common/alerts';
import {useSuspenseQuery} from "@tanstack/react-query";
import {OwnershipService} from "../services/ownership-service";
import {Badge, List, ListItem, ListItemButton, ListItemIcon, ListItemText} from "@mui/material";
import {useBreederInfo} from "../hooks/use-breeders";
import {AddCircle, Pets, RemoveCircle} from "@mui/icons-material";
import {Widget} from "../components/Common";
import {useNav} from "../components/Common/hooks/use-nav";
import RouteService from "../services/route-service";
import {RegistryDto$BreederRef} from "../api/generated/registry-service";

const useTransfers = () => useSuspenseQuery({
    queryKey: ['ownership-transfers'],
    queryFn: async () => await OwnershipService.listTransfers(),
});

const breederName = (breeder: RegistryDto$BreederRef) => {
    return [breeder.herdCode, breeder.farmName].filter(x => !!x).join('/');
}

const TransferIcon = ({owned}: { owned: boolean }) => (
    <Badge badgeContent={owned
        ? <AddCircle color={owned ? "success" : "info"}/>
        : <RemoveCircle color={owned ? "success" : "info"}/>}>
        <Pets fontSize="large"/>
    </Badge>
);

const OwnershipTransfersPage: React.FC = () => {
    const nav = useNav();
    const {data: breederInfo} = useBreederInfo();
    const {data: transfers} = useTransfers();

    if (transfers.length === 0) {
        return (
            <FlexStack>
                <Widget displayName="Transfers">
                    <InfoBox>Keine Transfers erfasst</InfoBox>
                </Widget>
            </FlexStack>
        );
    }

    return (
        <FlexStack>
            <Widget displayName="Transfers">
                <List>
                    {transfers.map(transfer => {
                        const isOwned = transfer.newOwner.id === breederInfo.id;
                        return (
                            <ListItem>
                                <ListItemButton
                                    onClick={() => isOwned
                                        ? nav.push(RouteService.ANIMAL_DETAILS, {panonId: transfer.panonId})
                                        : nav.push(RouteService.BREEDER_DETAILS, {breederId: transfer.newOwner.id})}>
                                    <ListItemIcon>
                                        <TransferIcon owned={isOwned}/>
                                    </ListItemIcon>
                                    <ListItemText secondary={transfer.transferDate.toLocaleString()}>
                                        <div>{transfer.animalName}</div>
                                        <div>{isOwned ? `von ${breederName(transfer.previousOwner)}` : `zu ${breederName(transfer.newOwner)}`}</div>
                                    </ListItemText>
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </Widget>
        </FlexStack>
    );
};

export default withErrorBoundary(
    OwnershipTransfersPage,
    {
        fallback: <ErrorBox>Fehler beim Anzeigen der Transfers</ErrorBox>,
    }
);
